import { useState } from 'react';
import { useUser } from '../contexts/UserContext';

/**
 * Shown while Microsoft sign-in succeeded but the TAP email verification code is still required.
 */
export default function EmailOtpGate() {
  const { emailOtpPending, submitEntraEmailOtp, resendEntraEmailOtp, clearEmailOtpFlow } = useUser();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (!emailOtpPending) {
    return null;
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = code.replace(/\D/g, '').slice(0, 6);
    if (trimmed.length !== 6) {
      setErr('Enter the 6-digit code from your email.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await submitEntraEmailOtp(trimmed);
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? String((e as { response?: { data?: { message?: string; error?: string } } }).response?.data?.message ||
              (e as { response?: { data?: { error?: string } } }).response?.data?.error ||
              'Verification failed')
          : e instanceof Error
            ? e.message
            : 'Verification failed';
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        fontFamily: 'var(--font-family)',
        padding: '24px',
        boxSizing: 'border-box',
      }}
    >
      <div
        className="card"
        style={{
          maxWidth: '400px',
          width: '100%',
          padding: '40px 32px',
          textAlign: 'center',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          borderRadius: '12px',
        }}
      >
        <span className="material-symbols-outlined" style={{ fontSize: '48px', color: '#FC5003' }}>
          mark_email_read
        </span>
        <h1 style={{ color: 'var(--secondary-color)', fontSize: '1.5rem', marginTop: '16px' }}>
          Check your email
        </h1>
        <p style={{ color: '#6c757d', marginTop: '12px', lineHeight: 1.5 }}>
          We sent a 6-digit code to <strong>{emailOtpPending.maskedEmail}</strong>. Enter it below to finish signing in.
        </p>

        {err && (
          <div
            style={{
              marginTop: '20px',
              padding: '12px',
              backgroundColor: '#f8d7da',
              border: '1px solid #f5c6cb',
              borderRadius: '8px',
              color: '#721c24',
              fontSize: '0.9rem',
            }}
          >
            {err}
          </div>
        )}

        <form onSubmit={onSubmit} style={{ marginTop: '24px', textAlign: 'left' }}>
          <label htmlFor="entra-otp-code" style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: 'var(--secondary-color)' }}>
            Verification code
          </label>
          <input
            id="entra-otp-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            style={{
              width: '100%',
              padding: '14px',
              fontSize: '1.4rem',
              letterSpacing: '0.4em',
              textAlign: 'center',
              borderRadius: '8px',
              border: '1px solid #ced4da',
              boxSizing: 'border-box',
            }}
          />
          <button
            type="submit"
            disabled={busy}
            style={{
              width: '100%',
              marginTop: '20px',
              padding: '14px',
              fontSize: '1.05rem',
              fontWeight: 600,
              border: '1px solid #FC5003',
              color: '#fff',
              background: busy ? '#ccc' : '#FC5003',
              borderRadius: '8px',
              cursor: busy ? 'not-allowed' : 'pointer',
            }}
          >
            {busy ? 'Verifying…' : 'Verify and continue'}
          </button>
        </form>

        <button
          type="button"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setErr(null);
            try {
              await resendEntraEmailOtp();
            } catch (e: unknown) {
              setErr(e instanceof Error ? e.message : 'Could not resend code.');
            } finally {
              setBusy(false);
            }
          }}
          style={{
            marginTop: '16px',
            background: 'none',
            border: 'none',
            color: '#FC5003',
            fontWeight: 600,
            cursor: busy ? 'not-allowed' : 'pointer',
            fontSize: '0.95rem',
          }}
        >
          Resend code
        </button>

        <button
          type="button"
          disabled={busy}
          onClick={() => void clearEmailOtpFlow()}
          style={{
            marginTop: '24px',
            background: 'none',
            border: 'none',
            color: '#6c757d',
            cursor: busy ? 'not-allowed' : 'pointer',
            fontSize: '0.9rem',
          }}
        >
          Cancel and use a different account
        </button>
      </div>
    </div>
  );
}
